/**
 * Platform Configuration Manager
 *
 * Provides access to platform configuration with business domain hierarchy
 * and maturity-based filtering capabilities.
 */

import { FeatureFlags } from "../feature-flags/FeatureFlags";
import debugLogger from "../utils/debugLogger";

import { loadPlatformConfig } from "./loadPlatformConfig";
import {
  PlatformConfig,
  BusinessDomainMetadata,
  FeatureMetadata,
  ComponentMetadata,
  RouteMetadata,
  MaturityLevel,
} from "./types";

export class PlatformConfigManager {
  private config: PlatformConfig | null = null;
  private businessDomains: Map<string, BusinessDomainMetadata> = new Map();
  private components: Map<string, ComponentMetadata> = new Map();
  private routes: Map<string, RouteMetadata> = new Map();
  private features: Map<string, FeatureMetadata> = new Map();
  private featureFlags: FeatureFlags;
  private initialized: boolean = false;

  constructor(featureFlags: FeatureFlags) {
    this.featureFlags = featureFlags;
  }

  /**
   * Initialize platform configuration from YAML file
   * @param configPath Path to platform.config.yml (relative to public/)
   *                  defaults to `/config/platform.config.yml` after reorganization
   */
  public async initialize(
    configPath: string = "/config/platform.config.yml",
  ): Promise<void> {
    if (this.initialized) {
      debugLogger.warn("PlatformConfigManager already initialized.");
      return;
    }

    try {
      this.config = await loadPlatformConfig(configPath);
      this.buildMetadata();
      this.initialized = true;
      debugLogger.info("PlatformConfigManager initialized successfully.");
    } catch (error) {
      debugLogger.error("Failed to initialize PlatformConfigManager:", error);
      throw error;
    }
  }

  /**
   * Build metadata structures for efficient lookup
   */
  private buildMetadata(): void {
    if (!this.config) return;

    this.businessDomains.clear();
    this.components.clear();
    this.routes.clear();
    this.features.clear();

    this.config.platform.businessDomains.forEach((domain) => {
      const domainMetadata: BusinessDomainMetadata = {
        name: domain.name,
        description: domain.description,
        features: [],
      };

      domain.features.forEach((feature) => {
        const componentMetadata: ComponentMetadata[] = feature.components.map(
          (component) => ({
            name: component.name,
            path: component.path,
            maturity: component.maturity as MaturityLevel,
            featureFlags: component.featureFlags || [],
            description: component.description,
            businessDomain: domain.name,
            feature: feature.name,
          }),
        );

        const routeMetadata: RouteMetadata[] = feature.routes.map((route) => ({
          path: route.path,
          component: route.component,
          maturity: route.maturity as MaturityLevel,
          featureFlags: route.featureFlags || [],
          description: route.description,
          businessDomain: domain.name,
          feature: feature.name,
        }));

        const featureMetadata: FeatureMetadata = {
          name: feature.name,
          description: feature.description,
          maturity: feature.maturity as MaturityLevel,
          businessDomain: domain.name,
          components: componentMetadata,
          routes: routeMetadata,
        };

        domainMetadata.features.push(featureMetadata);
        this.features.set(`${domain.name}.${feature.name}`, featureMetadata);

        // Index components
        componentMetadata.forEach((component) => {
          this.components.set(component.name, component);
        });

        // Index routes
        routeMetadata.forEach((route) => {
          this.routes.set(route.path, route);
        });
      });

      this.businessDomains.set(domain.name, domainMetadata);
    });
  }

  /**
   * Get all business domains
   */
  public getBusinessDomains(): BusinessDomainMetadata[] {
    this.ensureInitialized();
    return Array.from(this.businessDomains.values());
  }

  /**
   * Get business domain by name
   */
  public getBusinessDomain(name: string): BusinessDomainMetadata | undefined {
    this.ensureInitialized();
    return this.businessDomains.get(name);
  }

  /**
   * Get feature by business domain and feature name
   */
  public getFeature(
    businessDomain: string,
    featureName: string,
  ): FeatureMetadata | undefined {
    this.ensureInitialized();
    return this.features.get(`${businessDomain}.${featureName}`);
  }

  /**
   * Get component metadata by name
   */
  public getComponent(name: string): ComponentMetadata | undefined {
    this.ensureInitialized();
    return this.components.get(name);
  }

  /**
   * Get route metadata by path
   */
  public getRoute(path: string): RouteMetadata | undefined {
    this.ensureInitialized();
    return this.routes.get(path);
  }

  /**
   * Check if component should be enabled based on maturity and feature flags
   */
  public isComponentEnabled(componentName: string, userId?: string): boolean {
    const component = this.getComponent(componentName);
    if (!component) return false;

    return this.isEnabled(component.maturity, component.featureFlags, userId);
  }

  /**
   * Check if route should be enabled based on maturity and feature flags
   */
  public isRouteEnabled(routePath: string, userId?: string): boolean {
    const route = this.getRoute(routePath);
    if (!route) return false;

    return this.isEnabled(route.maturity, route.featureFlags, userId);
  }

  /**
   * Check if feature should be enabled based on maturity and feature flags
   */
  public isFeatureEnabled(
    businessDomain: string,
    featureName: string,
    userId?: string,
  ): boolean {
    const feature = this.getFeature(businessDomain, featureName);
    if (!feature) return false;

    return this.isEnabled(feature.maturity, [], userId);
  }

  /**
   * Internal method to check if something is enabled based on maturity and flags
   */
  private isEnabled(
    maturity: MaturityLevel,
    featureFlags: string[],
    userId?: string,
  ): boolean {
    if (!this.config) return false;

    // Get maturity level config
    const maturityConfig = this.config.platform.maturityLevels.find(
      (ml) => ml.level === maturity,
    );

    if (!maturityConfig) return false;

    // Check maturity default
    if (!maturityConfig.defaultEnabled) {
      // If maturity default is false, check if any feature flags are enabled
      if (featureFlags.length === 0) return false;
      return featureFlags.some((flag) =>
        this.featureFlags.isEnabled(flag, userId),
      );
    }

    // If maturity default is true, check feature flags (all must be enabled if any are specified)
    if (featureFlags.length > 0) {
      return featureFlags.every((flag) =>
        this.featureFlags.isEnabled(flag, userId),
      );
    }

    return true;
  }

  /**
   * Get all components filtered by maturity level
   */
  public getComponentsByMaturity(maturity: MaturityLevel): ComponentMetadata[] {
    this.ensureInitialized();
    return Array.from(this.components.values()).filter(
      (component) => component.maturity === maturity,
    );
  }

  /**
   * Get all routes filtered by maturity level
   */
  public getRoutesByMaturity(maturity: MaturityLevel): RouteMetadata[] {
    this.ensureInitialized();
    return Array.from(this.routes.values()).filter(
      (route) => route.maturity === maturity,
    );
  }

  /**
   * Get all enabled components for a business domain
   */
  public getEnabledComponents(
    businessDomain: string,
    userId?: string,
  ): ComponentMetadata[] {
    this.ensureInitialized();
    const domain = this.getBusinessDomain(businessDomain);
    if (!domain) return [];

    const enabledComponents: ComponentMetadata[] = [];
    domain.features.forEach((feature) => {
      feature.components.forEach((component) => {
        if (this.isComponentEnabled(component.name, userId)) {
          enabledComponents.push(component);
        }
      });
    });

    return enabledComponents;
  }

  /**
   * Get all enabled routes for a business domain
   */
  public getEnabledRoutes(
    businessDomain: string,
    userId?: string,
  ): RouteMetadata[] {
    this.ensureInitialized();
    const domain = this.getBusinessDomain(businessDomain);
    if (!domain) return [];

    const enabledRoutes: RouteMetadata[] = [];
    domain.features.forEach((feature) => {
      feature.routes.forEach((route) => {
        if (this.isRouteEnabled(route.path, userId)) {
          enabledRoutes.push(route);
        }
      });
    });

    return enabledRoutes;
  }

  /**
   * Get raw platform configuration
   */
  public getConfig(): PlatformConfig | null {
    return this.config;
  }

  /**
   * Check if initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Ensure platform config is initialized
   */
  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "PlatformConfigManager is not initialized. Call initialize() first.",
      );
    }
  }
}
